import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { NotificationService } from '../../services/notification.service';
import { FunctionService } from '../../services/function.service';
import { BaseComponent } from '../../base/base.component';
import { Dealer } from '../../interfaces/anagraphic.interfaces';
import { environment } from '../../../environments/environment';

interface InsertResult {
  totalRows: number;
  inserted: number;
  skipped: number;
  errors: number;
  errorDetails: string[];
  insertedDealers: Dealer[];
  skippedDealers: Dealer[];
}

type RowStatus = 'inserted' | 'skipped' | 'error';

interface DealerRow extends Dealer {
  rowStatus: RowStatus;
}

interface DuplicatePair {
  rowA: number;
  dealerCodeA: string;
  dealerNameA: string;
  dealerLegalAddressStreetA: string;
  dealerLegalAddressZipA: string;
  dealerLegalAddressCityA: string;
  dealerLegalAddressProvinceA: string;
  rowB: number;
  dealerCodeB: string;
  dealerNameB: string;
  dealerLegalAddressStreetB: string;
  dealerLegalAddressZipB: string;
  dealerLegalAddressCityB: string;
  dealerLegalAddressProvinceB: string;
  similarity: number;
  similarityName: number;
  similarityStreet: number;
  similarityZip: number;
  similarityCity: number;
  similarityProvince: number;
}

interface CheckDuplicatesResult {
  totalRows: number;
  pairsFound: number;
  threshold: number;
  pairs: DuplicatePair[];
}

@Component({
  selector: 'app-dealer-massive-insert',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dealer-massive-insert.component.html',
  styleUrl: './dealer-massive-insert.component.css'
})
export class DealerMassiveInsertComponent extends BaseComponent implements OnInit {

  // Upload
  selectedFile: File | null = null;
  isDragOver = false;
  isUploading = false;
  isDownloadingTemplate = false;
  result: InsertResult | null = null;
  showErrorDetails = false;
  clientCode = '';
  brandCode = '';

  // Table
  allRows: DealerRow[] = [];
  filteredRows: DealerRow[] = [];
  searchTerm = '';
  sortColumn = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  currentPage = 1;
  itemsPerPage = 25;
  filterStatus: 'all' | RowStatus = 'all';

  // Duplicates
  isCheckingDuplicates = false;
  showDuplicatesView = false;
  duplicatePairs: DuplicatePair[] = [];
  similarityThreshold = 80;

  private readonly apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    functionService: FunctionService,
    router: Router
  ) {
    super(functionService, router);
  }

  ngOnInit(): void {}

  // ── Upload ──────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) this.setFile(files[0]);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.setFile(input.files[0]);
  }

  private setFile(file: File): void {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xls') && !name.endsWith('.xlsx')) {
      this.notificationService.error('Il file deve essere in formato Excel (.xls o .xlsx)');
      return;
    }
    this.selectedFile = file;
    this.result = null;
    this.allRows = [];
    this.filteredRows = [];
    this.filterStatus = 'all';
    this.showDuplicatesView = false;
    this.duplicatePairs = [];
  }

  removeFile(): void {
    this.selectedFile = null;
    this.result = null;
    this.allRows = [];
    this.filteredRows = [];
    this.filterStatus = 'all';
    this.showDuplicatesView = false;
    this.duplicatePairs = [];
  }

  uploadFile(): void {
    if (!this.selectedFile) return;
    this.isUploading = true;

    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    if (this.clientCode.trim()) formData.append('clientCode', this.clientCode.trim());
    if (this.brandCode.trim()) formData.append('brandCode', this.brandCode.trim());
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http.post<InsertResult>(`${this.apiUrl}/dealers/massive-insert`, formData, { headers })
      .subscribe({
        next: (res) => {
          this.result = res;
          this.isUploading = false;
          this.buildRows(res);
          if (res.errors === 0) {
            this.notificationService.success(`Importazione completata: ${res.inserted} dealer inseriti`);
          } else {
            this.notificationService.error(`Importazione completata con ${res.errors} errori`);
          }
        },
        error: (err) => {
          this.isUploading = false;
          const msg = err?.error?.message || err?.message || 'Errore durante il caricamento';
          this.notificationService.error(msg);
        }
      });
  }

  private buildRows(res: InsertResult): void {
    const inserted: DealerRow[] = (res.insertedDealers ?? []).map(d => ({ ...d, rowStatus: 'inserted' as RowStatus }));
    const skipped: DealerRow[] = (res.skippedDealers ?? []).map(d => ({ ...d, rowStatus: 'skipped' as RowStatus }));
    this.allRows = [...inserted, ...skipped];
    this.applyFilter();
  }

  downloadTemplate(): void {
    this.isDownloadingTemplate = true;
    const token = localStorage.getItem('accessToken');
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http.get(`${this.apiUrl}/dealers/massive-insert/template`, { headers, responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'dealer_massive_insert_template.xlsx';
          a.click();
          window.URL.revokeObjectURL(url);
          this.isDownloadingTemplate = false;
          this.notificationService.success('Template scaricato con successo');
        },
        error: () => {
          this.isDownloadingTemplate = false;
          this.notificationService.error('Errore durante il download del template');
        }
      });
  }

  toggleErrorDetails(): void {
    this.showErrorDetails = !this.showErrorDetails;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ── Duplicates ──────────────────────────────────────────

  checkDuplicates(): void {
    if (!this.selectedFile || this.isCheckingDuplicates) return;
    this.isCheckingDuplicates = true;

    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    if (this.clientCode.trim()) formData.append('clientCode', this.clientCode.trim());
    if (this.brandCode.trim()) formData.append('brandCode', this.brandCode.trim());
    formData.append('threshold', (this.similarityThreshold / 100).toString());
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http.post<CheckDuplicatesResult>(
      `${this.apiUrl}/dealers/massive-insert/check-duplicates`, formData, { headers }
    ).subscribe({
      next: (res) => {
        this.duplicatePairs = res.pairs;
        this.showDuplicatesView = true;
        this.isCheckingDuplicates = false;
        if (res.pairsFound === 0) {
          this.notificationService.success(`Nessun possibile duplicato trovato (soglia ${this.similarityThreshold}%)`);
        } else {
          this.notificationService.error(`Trovate ${res.pairsFound} coppie con similarità ≥ ${this.similarityThreshold}%`);
        }
      },
      error: (err) => {
        this.isCheckingDuplicates = false;
        const msg = err?.error?.message || err?.message || 'Errore durante il controllo duplicati';
        this.notificationService.error(msg);
      }
    });
  }

  exitDuplicatesView(): void {
    this.showDuplicatesView = false;
  }

  exportDuplicates(): void {
    const data = this.duplicatePairs.map((p: DuplicatePair, i: number) => ({
      '#': i + 1,
      'Similarità %': Math.round(p.similarity * 100),
      'Riga A': p.rowA,
      'Codice A': p.dealerCodeA,
      'Nome A': p.dealerNameA,
      'Indirizzo A': p.dealerLegalAddressStreetA,
      'CAP A': p.dealerLegalAddressZipA,
      'Città A': p.dealerLegalAddressCityA,
      'Prov. A': p.dealerLegalAddressProvinceA,
      'Riga B': p.rowB,
      'Codice B': p.dealerCodeB,
      'Nome B': p.dealerNameB,
      'Indirizzo B': p.dealerLegalAddressStreetB,
      'CAP B': p.dealerLegalAddressZipB,
      'Città B': p.dealerLegalAddressCityB,
      'Prov. B': p.dealerLegalAddressProvinceB,
      'Sim. Nome %': Math.round(p.similarityName * 100),
      'Sim. Indirizzo %': Math.round(p.similarityStreet * 100),
      'Sim. CAP %': Math.round(p.similarityZip * 100),
      'Sim. Città %': Math.round(p.similarityCity * 100),
      'Sim. Prov. %': Math.round(p.similarityProvince * 100),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Duplicati');
    XLSX.writeFile(wb, `duplicati_dealer_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  simPercent(value: number): string {
    return (value * 100).toFixed(0) + '%';
  }

  simClass(value: number): string {
    if (value >= 0.95) return 'sim-very-high';
    if (value >= 0.90) return 'sim-high';
    return 'sim-medium';
  }

  // ── Table ───────────────────────────────────────────────

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredRows = this.allRows.filter(r => {
      const matchStatus = this.filterStatus === 'all' || r.rowStatus === this.filterStatus;
      const matchTerm = !term || [r.dealerCode, r.dealerName, r.dealerClientCode, r.dealerLegalAddressCity, r.dealerVatNumber]
        .some(v => (v || '').toLowerCase().includes(term));
      return matchStatus && matchTerm;
    });
    if (this.sortColumn) this.applySortInternal();
    this.currentPage = 1;
  }

  setFilterStatus(s: typeof this.filterStatus): void {
    this.filterStatus = s;
    this.applyFilter();
  }

  sortBy(col: string): void {
    if (this.sortColumn === col) {
      if (this.sortDirection === 'asc') this.sortDirection = 'desc';
      else { this.sortColumn = ''; this.sortDirection = ''; }
    } else {
      this.sortColumn = col;
      this.sortDirection = 'asc';
    }
    this.applySortInternal();
  }

  private applySortInternal(): void {
    if (!this.sortColumn || !this.sortDirection) return;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredRows.sort((a: any, b: any) => {
      const va = (a[this.sortColumn] ?? '').toString().toLowerCase();
      const vb = (b[this.sortColumn] ?? '').toString().toLowerCase();
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.itemsPerPage));
  }

  get paginatedRows(): DealerRow[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredRows.slice(start, start + this.itemsPerPage);
  }

  getPageNumbers(): number[] {
    const max = 5, total = this.totalPages;
    if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
    const half = Math.floor(max / 2);
    let start = Math.max(1, this.currentPage - half);
    const end = Math.min(total, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) this.currentPage = page;
  }

  setItemsPerPage(n: number): void {
    this.itemsPerPage = n;
    this.currentPage = 1;
  }

  getSortIcon(col: string): string {
    if (this.sortColumn !== col) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }
}
